import { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

interface UserAvatarProps {
  tenantId: string;
  userId: string;
  userName: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  xs: 'h-5 w-5 text-[9px]',
  sm: 'h-6 w-6 text-[10px]',
  md: 'h-8 w-8 text-xs',
  lg: 'h-16 w-16 text-lg',
};

/**
 * Displays a user's avatar as a miniature profile picture.
 * Falls back to initials on a colored background if no avatar exists.
 */
export function UserAvatar({ tenantId, userId, userName, size = 'sm', className = '' }: UserAvatarProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoke: string | null = null;

    apiClient.getUserAvatarBlobUrl(tenantId, userId).then((url) => {
      if (url) {
        revoke = url;
        setAvatarUrl(url);
      }
    });

    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tenantId, userId]);

  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Deterministic color from userId
  const hash = userId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500',
    'bg-indigo-500', 'bg-teal-500', 'bg-orange-500', 'bg-cyan-500',
  ];
  const bgColor = colors[hash % colors.length];

  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt={userName}
        className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0 ${className}`}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className={`${sizeClasses[size]} ${bgColor} rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 ${className}`}
      title={userName}
    >
      {initials}
    </span>
  );
}
